import NoResults from '@common-assets/lottie/no-results.json';
import { Stack } from "@mui/material";
import Lottie from "@/common/components/lazy-lottie/lazy-lottie";
import React, { useEffect, useRef, useState } from 'react';
import { isClearFlow } from '@common/utils/clear-flow';
import ClearEmptyIllustration from '@common/components/clear-schedule-panel/clear-empty-illustration';
import { useTranslation } from 'react-i18next';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

interface ScheduledMessagesEmptyStateProps {
    message?: string;
    /**
     * `empty` = this account has nothing scheduled (a first run).
     * `no-matches` = there ARE messages, the current search/filters just hide them.
     * They are different situations and must not share one sentence: the old copy
     * ("we couldn't find scheduled messages") read as a failed search to someone
     * who had simply never scheduled anything — and the English key was missing
     * altogether, so English users saw the raw key string.
     */
    variant?: 'empty' | 'no-matches' | 'sent-hidden';
    /** `sent-hidden` = nothing narrows the view, "Show sent" is off, and what is left is
     *  history. Not "no matches": clearing filters would not reveal it — only "Show sent"
     *  does, so that is the action this variant offers. Said without a count: counting it
     *  would mean fetching sent sends the user has not asked to see. */
    onShowSent?: () => void;
    /** When given, a first-run empty state offers the action instead of just
     *  describing the void. */
    onCreate?: () => void;
}

/** Below this panel height there is no room for a readable illustration. */
const MIN_ART_SPACE = 300;

export default function ScheduledMessagesEmptyState({
    message,
    variant = 'empty',
    onCreate,
    onShowSent,
}: ScheduledMessagesEmptyStateProps) {
    const { t } = useTranslation('common');

    // Fits the space it is given instead of scrolling: the copy and the action keep
    // their size, the illustration shrinks with the available height, and below
    // MIN_ART_SPACE it is dropped altogether rather than shown as a speck.
    const rootRef = useRef<HTMLDivElement>(null);
    const [showArt, setShowArt] = useState(true);
    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => setShowArt(el.clientHeight >= MIN_ART_SPACE));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const title = message
        ?? (variant === 'no-matches'
            ? t('scheduledMessagesEmptyState.noMatches', 'No messages match your filters')
            : variant === 'sent-hidden'
                ? t('scheduledMessagesEmptyState.nothingLeft', 'Nothing left to send')
                : t('scheduledMessagesEmptyState.title', 'Nothing scheduled yet'));

    const body = message
        ? null
        : (variant === 'no-matches'
            ? t('scheduledMessagesEmptyState.noMatchesBody', 'Try clearing the search or the filters.')
            : variant === 'sent-hidden'
                ? t('scheduledMessagesEmptyState.sentHiddenBody', 'Sends that already went out are hidden.')
                : t('scheduledMessagesEmptyState.body', 'Messages you schedule will show up here.'));

    return (
        <Stack
            ref={rootRef}
            alignItems="center"
            justifyContent="center"
            style={{ color: 'var(--primary)', padding: '16px 24px', textAlign: 'center', height: '100%', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box' }}
        >
            {/* The shared Lottie is Blueticks blue/yellow; the clear flow gets art
                in its own palette. Copy and actions below are identical. */}
            {showArt && <div style={{ flex: '0 1 250px', minHeight: 0, width: 250, maxWidth: '100%', display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                {isClearFlow() ? (
                    <ClearEmptyIllustration />
                ) : (
                    <Lottie
                        autoPlay={true}
                        loop={true}
                        animationData={NoResults}
                        style={{ width: '100%', height: '100%' }}
                    />
                )}
            </div>}
            <div style={{ fontWeight: 600, marginTop: 8, flexShrink: 0 }}>{title}</div>
            {body && (
                <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted, #6b7280)', flexShrink: 0 }}>
                    {body}
                </div>
            )}
            {variant === 'sent-hidden' && onShowSent && (
                <button
                    type="button"
                    onClick={onShowSent}
                    style={{
                        marginTop: 12,
                        flexShrink: 0,
                        border: 0,
                        background: 'transparent',
                        padding: '4px 8px',
                        font: 'inherit',
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--cal-brand-ink, var(--primary))',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    }}
                >
                    {t('calendarSidebarFilters.showSent.title', 'Show sent')}
                </button>
            )}
            {variant === 'empty' && onCreate && (
                <button
                    type="button"
                    onClick={onCreate}
                    style={{
                        marginTop: 14,
                        flexShrink: 0,
                        height: 36,
                        padding: '0 16px',
                        borderRadius: 10,
                        border: 'none',
                        background: 'var(--cta-button-bg, #0369A1)',
                        color: '#fff',
                        font: 'inherit',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    {t('scheduledMessagesEmptyState.cta', 'Schedule your first message')}
                </button>
            )}
            {variant === 'empty' && onCreate && (
                <GuideVideoButton lesson="message-planner" variant="link" surface="planner_empty" style={{ marginTop: 10 }} />
            )}
        </Stack>
    );
}
